/**
 * Adresa odborníka a mapa k ní.
 *
 * Odborníci mají geografii dvakrát: původní `address`/`city` a novější
 * `location` z Bloom Worldu. Běží vedle sebe, takže tyhle funkce sáhnou po
 * tom, co je vyplněné — jinak by česká stránka a stránka země ukazovaly
 * u jednoho odborníka pokaždé něco jiného.
 */

/**
 * Název kategorie k zobrazení.
 *
 * Správkyně může ke kategorii nastavit kratší štítek, ať se dlouhý název
 * („Psychologie, psychiatrie a sexuologie") vejde na kartu. Celý název je
 * jedno jméno, ne seznam – nekrátí se po čárkách, na to je `short_label`.
 */
export const stitekKategorie = (obor, kategorie = []) =>
  kategorie.find((k) => k.name === obor)?.short_label || obor;

/** Ulice, pokud ji známe. */
export const uliceOdbornika = (o) => o?.location?.address || o?.address || '';

/** Město, pokud ho známe. */
export const mestoOdbornika = (o) => o?.location?.city_name || o?.city || '';

/** Adresa na jeden řádek: „Ulice 1, Město". */
export const adresaOdbornika = (o) =>
  [uliceOdbornika(o), mestoOdbornika(o)].filter(Boolean).join(', ');

/**
 * Kraj – jen když je to opravdu kraj.
 *
 * Odborníci mimo Česko mají `country: 'WORLD'` a v `region` uloženo „Svět".
 * Je to přihrádka z doby, kdy se svět vešel do jedné položky, ne místo. Pod
 * adresou z toho vylezlo „San Francisco, Svět", což vypadá jako rozbitá data.
 * Poznáváme to podle `country`, ne podle slova „Svět“ – to se s překlady mění.
 */
export const regionOdbornika = (o) =>
  (o?.country === 'WORLD' ? '' : (o?.region || ''));

/** Dotaz pro mapu. Se jménem, protože klinika se hledá líp podle názvu. */
export const dotazDoMapy = (o) => [o?.name, uliceOdbornika(o), mestoOdbornika(o)]
  .filter(Boolean).join(', ');

/** Odkaz „Otevřít v mapách" — otevře se v mapách, které má člověk rád. */
export const odkazDoMap = (o) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(dotazDoMapy(o))}`;

/**
 * Zdroj vložené mapy.
 *
 * S vlastním klíčem jede oficiální Embed API. Bez klíče se použije varianta,
 * která klíč nepotřebuje, aby mapa fungovala i tam, kde klíč nikdo nenastavil.
 * Když neznáme ani adresu, nevrací se nic a mapa se nekreslí — prázdný rámeček
 * s hláškou od Googlu vypadá jako rozbitá stránka.
 */
export const mapaOdbornika = (o) => {
  const dotaz = adresaOdbornika(o);
  if (!dotaz) return '';
  const q = encodeURIComponent(dotaz);
  const klic = process.env.REACT_APP_GOOGLE_MAPS_KEY;
  return klic
    ? `https://www.google.com/maps/embed/v1/place?key=${klic}&q=${q}&zoom=15`
    : `https://maps.google.com/maps?q=${q}&output=embed`;
};
